import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { CheckCircle2, FileText, Edit2, Trash2, ShieldCheck } from "lucide-react";

// Helper: Truncate text for table cells
const truncate = (str, max = 60) => str?.length > max ? str.slice(0, max) + "..." : str;

const COMPLAINTS_API_URL = 'https://portal.lsb.go.ke/lsb/staff-complaints/';

const initialFormState = { title: '', message: '', document: null, documentName: '', complaint_id: null, existingDocumentUrl: null, existingDocumentName: '', is_resolved: false, licensed: '', subject: '' };

const ComplaintsTab = () => {
    const [form, setForm] = useState(initialFormState);
    const [showModal, setShowModal] = useState(false);
    const [complaints, setComplaints] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [lastRefresh, setLastRefresh] = useState(Date.now());

    // Fetch Complaints
    const fetchComplaints = async () => {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
            setFetchError("Unauthorized: No access token found.");
            return;
        }

        setLoading(true);
        setFetchError('');
        try {
            const res = await fetch(COMPLAINTS_API_URL, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!res.ok) {
                if (res.status === 401 || res.status === 403) throw new Error("Unauthorized: You do not have permission to view complaints.");
                throw new Error("Failed to fetch complaints");
            }
            const data = await res.json();
            const rawComplaints = Array.isArray(data) ? data : (data?.results || []);
            // Normalize: Ensure every item has a complaint_id (fallback to id)
            const normalized = rawComplaints.map(n => ({
                ...n,
                complaint_id: n.complaint_id || n.id
            }));
            setComplaints(normalized);
            setLastRefresh(Date.now()); // <--- fix: update lastRefresh after data is fetched
        } catch (err) {
            setFetchError(err.message || "Unable to fetch complaints.");
        } finally {
            setLoading(false);
        }
    };

    // Set up auto-refresh every 15s
    useEffect(() => {
        fetchComplaints();
        const interval = setInterval(() => {
            fetchComplaints();
        }, 15000);

        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, []);

    // Modal open to "Manage" complaint
    const openManageModal = (n) => {
        setForm({
            title: n.title || '',
            message: n.message || '',
            document: null,
            documentName: '',
            complaint_id: n.complaint_id || n.id,
            existingDocumentUrl: n.document || null,
            existingDocumentName: n.document ? getFilenameFromUrl(n.document) : '',
            is_resolved: !!n.is_resolved,
            licensed: typeof n.licensed === 'number' ? n.licensed : (n.licensed || ''),
            subject: n.subject || ''
        });
        setShowModal(true);
    };

    // Helper: extract filename from URL
    function getFilenameFromUrl(url) {
        try {
            if (!url) return '';
            const u = decodeURIComponent(url.split('?')[0]);
            return u.substring(u.lastIndexOf('/') + 1);
        } catch {
            return '';
        }
    }

    // Resolve Complaint: send payload per backend spec
    const handleResolve = async () => {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
            await Swal.fire({ icon: 'error', title: 'Error', text: 'Unauthorized: Session expired.' });
            return;
        }

        const result = await Swal.fire({
            title: 'Resolve Complaint?',
            text: 'Are you sure you want to mark this complaint as resolved?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#059669',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, resolve it!',
            cancelButtonText: 'Cancel'
        });
        if (!result.isConfirmed) return;

        setSubmitting(true);

        // payload
        const putPayload = {
            complaint_id: form.complaint_id,
            licensed: typeof form.licensed === 'number'
                ? form.licensed
                : (form.licensed !== '' && !isNaN(Number(form.licensed)))
                    ? Number(form.licensed)
                    : form.licensed,
            subject: form.subject || form.title || "",
            message: form.message,
            is_resolved: true
        };

        try {
            const requestUrl = `${COMPLAINTS_API_URL}?complaint_id=${encodeURIComponent(form.complaint_id)}`;
            const res = await fetch(requestUrl, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(putPayload),
            });

            if (!res.ok) {
                let msg = 'Failed to resolve complaint.';
                if (res.status === 401 || res.status === 403) msg = "Unauthorized: You do not have permission to modify complaints.";
                try {
                    const data = await res.json();
                    if (data?.detail) msg = data.detail;
                } catch { }
                throw new Error(msg);
            }

            const resData = await res.json();
            const normalized = { ...resData, complaint_id: resData.complaint_id || resData.id || form.complaint_id };

            setComplaints(prev => prev.map(n => ((n.complaint_id === form.complaint_id || n.id === form.complaint_id) ? normalized : n)));

            setShowModal(false);
            setForm(initialFormState);
            await Swal.fire({
                icon: 'success',
                title: 'Resolved',
                text: 'The complaint has been marked as resolved.',
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err.message || "Error resolving complaint.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Delete Complaint -- Send complaint_id as payload in DELETE request
    const handleDelete = async (complaint_id) => {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
            await Swal.fire({ icon: 'error', title: 'Error', text: 'Unauthorized: Session expired.' });
            return;
        }

        // If called without argument, fallback to state's form.complaint_id
        const cid = complaint_id || form.complaint_id;
        if (!cid) {
            await Swal.fire({ icon: 'error', title: 'Error', text: 'Invalid Complaint ID' });
            return;
        }

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this complaint? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });
        if (!result.isConfirmed) return;

        try {
            // DELETE with complaint_id in payload (not URL)
            const deletePayload = { complaint_id: cid };
            const res = await fetch(COMPLAINTS_API_URL, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(deletePayload),
            });
            if (!res.ok) {
                let msg = 'Failed to delete.';
                if (res.status === 401 || res.status === 403) msg = "Unauthorized: You do not have permission to delete complaints.";
                try {
                    const data = await res.json();
                    if (data?.detail) msg = data.detail;
                } catch { }
                throw new Error(msg);
            }
            // Remove complaint with matching complaint_id only
            setComplaints(prev => prev.filter(n => n.complaint_id !== cid));

            if (showModal) setShowModal(false);

            await Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'The complaint has been deleted.',
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Delete Failed',
                text: err.message || 'Failed to delete complaint.',
            });
        }
    };

    return (
        <div className="max-w-7xl mx-auto">
            <div className="pb-3 flex flex-col justify-end text-xs leading-2 font-medium">
                <span>Auto-refresh: every 15s</span>
                <span className="" title={new Date(lastRefresh).toLocaleString()}>
                    Last refreshed: {new Date(lastRefresh).toLocaleTimeString()}
                </span>
            </div>

            {/* Management Modal */}
            {showModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm transition-all">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-0 overflow-hidden relative animate-in fade-in zoom-in duration-200">
                        <div className='bg-[#3d0707] p-6 text-white flex justify-between items-center'>
                            <h5 className="font-bold flex items-center gap-2">
                                <ShieldCheck className="w-6 h-6" /> Complaint Details
                            </h5>

                            <button
                                className="text-white/80 hover:text-white transition-colors group"
                                onClick={() => {
                                    setShowModal(false);
                                    setForm(initialFormState);
                                }}
                            >
                                <i className="fa-solid fa-xmark text-2xl transition-transform duration-200 group-hover:scale-125 group-hover:rotate-90" />
                            </button>
                        </div>

                        <div className="p-8">
                            <div className="flex gap-1 items-center">
                                <p className="text-sm font-bold uppercase tracking-wider text-gray-500">Complaint ID:</p>
                                <p className="font-bold capitalize">{form.complaint_id}</p>
                            </div>

                            <div className="flex gap-1 items-center">
                                <p className="text-sm font-bold uppercase tracking-wider text-gray-500">Status:</p>
                                <div className="flex gap-1 items-center">
                                    {form.is_resolved
                                        ? <p className="text-emerald-700 flex gap-1 items-center font-bold">Resolved</p>
                                        : <p className="text-amber-700 flex gap-1 items-center font-bold">Pending</p>
                                    }
                                </div>
                            </div>

                            <div className="flex gap-1 items-center">
                                <p className="text-sm font-bold uppercase tracking-wider text-gray-500">Subject:</p>
                                <p className="font-bold capitalize">{form.subject || form.title || "No Subject"}</p>
                            </div>

                            <div className="flex gap-1 items-center">
                                <p className="text-sm font-bold uppercase tracking-wider text-gray-500">Against (Licensed):</p>
                                <p className="font-bold capitalize">{String(form.licensed) || "Not Specified"}</p>
                            </div>

                            <div className="flex gap-1 items-center">
                                <p className="text-sm font-bold uppercase tracking-wider text-gray-500">Message:</p>
                                <p className="font-bold capitalize max-h-40 overflow-y-auto">{form.message}</p>
                            </div>

                            {form.existingDocumentUrl && (
                                <div className="space-y-2">
                                    <label className="text-sm font-bold uppercase tracking-wider text-gray-500">Evidence / Attachment</label>
                                    <a
                                        href={form.existingDocumentUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-100 rounded-xl text-indigo-700 hover:bg-indigo-100 transition"
                                    >
                                        <FileText className="w-6 h-6" />
                                        <div className="flex-1">
                                            <p className="text-sm font-bold truncate">{form.existingDocumentName}</p>
                                            <p className="text-[10px] uppercase font-bold text-indigo-400">Click to view document</p>
                                        </div>
                                    </a>
                                </div>
                            )}

                            <div className="pt-6 grid grid-cols-3 gap-4 border-t border-gray-100">
                                {!form.is_resolved && (
                                    <button
                                        onClick={handleResolve}
                                        disabled={submitting}
                                        className="flex-1 bg-emerald-600 text-white px-4 py-2 rounded-xl font-bold shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        <CheckCircle2 className="w-5 h-5" /> Resolve
                                    </button>
                                )}
                                <button
                                    onClick={() => handleDelete(form.complaint_id)}
                                    disabled={submitting}
                                    className="px-4 py-2 rounded-xl font-bold border-2 border-red-300 text-red-600 hover:bg-red-800 transition active:scale-95 disabled:opacity-100 flex items-center justify-center gap-2 hover:text-white"
                                >
                                    <Trash2 className="w-5 h-5" /> Delete
                                </button>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-500 font-bold hover:text-gray-700 transition border-2 border-gray-300 rounded-xl hover:bg-gray-800 transition active:scale-95 disabled:opacity-100 hover:text-white"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="py-2 px-4 text-left text-sm font-bold text-gray-500 uppercase tracking-wider">ID</th>
                                <th className="py-2 px-4 text-left text-sm font-bold text-gray-500 uppercase tracking-wider">Licensed</th>
                                <th className="py-2 px-4 text-left text-sm font-bold text-gray-500 uppercase tracking-wider">Subject</th>
                                <th className="py-2 px-4 text-left text-sm font-bold text-gray-500 uppercase tracking-wider">Message</th>
                                <th className="py-2 px-4 text-center text-sm font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="py-2 px-4 text-center text-sm font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="py-20 text-center">
                                        <div className="flex flex-col items-center">
                                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mb-4"></div>
                                            <p className="text-gray-500 font-medium">Fetching complaints...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : complaints.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-20 text-center">
                                        <div className="text-gray-400">
                                            <ShieldCheck className="w-12 h-12 mx-auto mb-2 opacity-20" />
                                            <p>No complaints found in the system.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                complaints.map(n => (
                                    <tr key={n.complaint_id || n.id} className="hover:bg-slate-50/50 transition-colors group">
                                        <td className="py-2 px-4 whitespace-nowrap">
                                            <span className="font-mono text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                {truncate(String(n.complaint_id || n.id), 8)}
                                            </span>
                                        </td>
                                        <td className="py-2 px-4 font-medium">{truncate(String(n.licensed), 30)}</td>
                                        <td className="py-2 px-4 font-medium">{truncate(n.subject || n.title, 30)}</td>
                                        <td className="py-2 px-4 font-medium">{truncate(n.message, 30)}</td>
                                        <td className="py-2 px-4 text-center">
                                            {n.is_resolved
                                                ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-bold bg-emerald-100 text-emerald-800">Resolved</span>
                                                : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-bold bg-amber-100 text-amber-800">Pending</span>
                                            }
                                        </td>
                                        <td className="py-2 px-4 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => openManageModal(n)}
                                                    className="p-2 text-green-600 hover:bg-indigo-50 rounded-lg transition"
                                                    title="Manage Complaint"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(n.complaint_id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ComplaintsTab;
